import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SubProject } from '../services/subProjectService';
import type { KnowledgeBase } from '../services/knowledgeBaseService';
import type { EntitySCV2Config } from '../configs/entitySCV2Config';
import type { WatchStatus } from '../services/techWatchService';
import { CreateKBModal } from './CreateKBModal';
import { TechWatchConfigModal } from './TechWatchConfigModal';
import { TechWatchDigestPanel } from './TechWatchDigestPanel';

interface SCV2ModalsProps {
  entityType: string | undefined;
  entityId: string | undefined;
  entityName: string;
  entityProjectId: string | undefined;
  config: EntitySCV2Config;
  canCreateSubprojects: boolean;

  showDeleteModal: boolean;
  onCloseDeleteModal: () => void;
  onConfirmDelete: () => Promise<void>;

  showSubProjectModal: boolean;
  onCloseSubProjectModal: () => void;
  subProjectFormData: { name: string; description: string };
  setSubProjectFormData: React.Dispatch<React.SetStateAction<{ name: string; description: string }>>;
  onSubmitCreateSubProject: (e: React.FormEvent) => Promise<void>;

  showViewSubProjectsModal: boolean;
  onCloseViewSubProjectsModal: () => void;
  onOpenSubProjectModal: () => void;
  subProjectsList: SubProject[];
  loadingSubProjects: boolean;
  onSubProjectCardClick: (id: string) => void;

  showSearchServiceModal: boolean;
  onCloseSearchServiceModal: () => void;
  onSelectSearchService: (service: string) => void;

  showAddDocModal: boolean;
  onCloseAddDocModal: () => void;
  uploadMode: 'patent' | 'scipub' | 'url' | 'other';
  setUploadMode: React.Dispatch<React.SetStateAction<'patent' | 'scipub' | 'url' | 'other'>>;
  selectedFiles: FileList | null;
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileList | null>>;
  pageUrl: string;
  setPageUrl: React.Dispatch<React.SetStateAction<string>>;
  uploading: boolean;
  onSubmitUpload: (e: React.FormEvent) => Promise<void>;

  showContextKbModal: boolean;
  onCloseContextKbModal: () => void;
  contextKbConfig: { context: string; defaultName: string; tabName: string } | null;
  onContextKbCreated: (kb: KnowledgeBase) => void;

  showTechWatchConfig: boolean;
  onCloseTechWatchConfig: () => void;
  onTechWatchSaved: (w: unknown) => void;
  onTechWatchDeleted: () => void;
  showTechWatchDigest: boolean;
  onCloseTechWatchDigest: () => void;
  onEditWatch: () => void;
  techWatchStatus: WatchStatus | null;
}

const SCV2Modals: React.FC<SCV2ModalsProps> = ({
  entityType, entityId, entityName, entityProjectId, config, canCreateSubprojects,
  showDeleteModal, onCloseDeleteModal, onConfirmDelete,
  showSubProjectModal, onCloseSubProjectModal, subProjectFormData, setSubProjectFormData, onSubmitCreateSubProject,
  showViewSubProjectsModal, onCloseViewSubProjectsModal, onOpenSubProjectModal, subProjectsList, loadingSubProjects, onSubProjectCardClick,
  showSearchServiceModal, onCloseSearchServiceModal, onSelectSearchService,
  showAddDocModal, onCloseAddDocModal, uploadMode, setUploadMode, selectedFiles, setSelectedFiles, pageUrl, setPageUrl, uploading, onSubmitUpload,
  showContextKbModal, onCloseContextKbModal, contextKbConfig, onContextKbCreated,
  showTechWatchConfig, onCloseTechWatchConfig, onTechWatchSaved, onTechWatchDeleted,
  showTechWatchDigest, onCloseTechWatchDigest, onEditWatch, techWatchStatus,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Context KB Creation Modal */}
      {showContextKbModal && contextKbConfig && entityType === 'sub-project' && (
        <CreateKBModal
          isOpen={showContextKbModal}
          onClose={onCloseContextKbModal}
          onCreated={onContextKbCreated}
          prefilledSubProjectId={entityId || null}
          prefilledProjectId={entityProjectId || null}
          prefilledName={contextKbConfig.defaultName}
          context={contextKbConfig.context}
        />
      )}

      {/* Delete Entity Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onCloseDeleteModal()}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2>Delete {config.displayName}</h2>
              <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={onCloseDeleteModal}>&times;</span>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="warning-message">
                <p>
                  Are you sure you want to delete <strong>{entityName}</strong>?
                  This action cannot be undone.
                  {entityType === 'project' && ' All the sub-projects will be deleted as well.'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button type="button" className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 disabled:opacity-50" onClick={onCloseDeleteModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button type="button" className="px-4 py-2 text-sm bg-destructive text-white rounded hover:opacity-90" onClick={onConfirmDelete}>
                {t('projectDetail.confirmDelete', 'I confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Sub-project Modal */}
      {showSubProjectModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onCloseSubProjectModal()}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2>{t('projectDetail.createSubProject', 'Create Sub-project')}</h2>
              <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={onCloseSubProjectModal}>&times;</span>
            </div>
            <form onSubmit={onSubmitCreateSubProject}>
              <div className="flex flex-col gap-1">
                <label>{t('projectDetail.subProjectName', 'Sub-project Name')}:</label>
                <input type="text" required maxLength={200} placeholder={t('projectDetail.subProjectPlaceholder', 'e.g., Battery Cooling System')} value={subProjectFormData.name} onChange={(e) => setSubProjectFormData({ ...subProjectFormData, name: e.target.value })} />
              </div>
              <div className="flex flex-col gap-1">
                <label>{t('projectDetail.description', 'Description')}:</label>
                <textarea rows={3} maxLength={1000} placeholder={t('projectDetail.subProjectDescPlaceholder', 'Describe the technical problem this sub-project addresses')} value={subProjectFormData.description} onChange={(e) => setSubProjectFormData({ ...subProjectFormData, description: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 disabled:opacity-50" onClick={onCloseSubProjectModal}>{t('common.cancel', 'Cancel')}</button>
                <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50">{t('projectDetail.createSubProjectBtn', 'Create Sub-project')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Sub-Projects Modal */}
      {showViewSubProjectsModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onCloseViewSubProjectsModal()}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2>Sub-projects of {entityName}</h2>
              <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={onCloseViewSubProjectsModal}>&times;</span>
            </div>
            <div className="px-5 py-4 space-y-4">
              {loadingSubProjects ? (
                <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
                  <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mr-2" />
                  Loading sub-projects...
                </div>
              ) : subProjectsList.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <p>No sub-projects found for this project.</p>
                  {canCreateSubprojects && (
                    <button className="action-btn btn-create" onClick={onOpenSubProjectModal}>
                      Create First Sub-project
                    </button>
                  )}
                </div>
              ) : (
                <div className="subproject-cards-grid">
                  {subProjectsList.map((subProject) => (
                    <div key={subProject.id} className="subproject-card" onClick={() => onSubProjectCardClick(subProject.id)}>
                      <div className="subproject-card-header">
                        <h3 className="subproject-card-title">{subProject.name}</h3>
                        {subProject.status && (
                          <span className={`status-badge status-${subProject.status.toLowerCase()}`}>
                            {subProject.status}
                          </span>
                        )}
                      </div>
                      <div className="subproject-card-body">
                        {subProject.problem_statement ? (
                          <p className="subproject-description">
                            {subProject.problem_statement.length > 100
                              ? `${subProject.problem_statement.substring(0, 100)}...`
                              : subProject.problem_statement}
                          </p>
                        ) : (
                          <p className="subproject-description text-muted">No description available</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search Service Selection Modal */}
      {showSearchServiceModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onCloseSearchServiceModal()}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2>Select Search Service</h2>
              <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={onCloseSearchServiceModal}>&times;</span>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p>Choose the type of search to perform:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem' }}>
                {/* KSA agent PARKED 2026-09-01 (founder decision): feature is broken/half-baked;
                    kept greyed out until a future eval-first rebuild. Do not re-enable without a fix. */}
                <button className="px-4 py-2 text-sm bg-primary-600 text-white rounded disabled:opacity-50 cursor-not-allowed" disabled style={{ padding: '1rem', textAlign: 'left' }}>
                  <strong>{t('searchService.patentSearch.button', 'EP Patent Search — AI Agent')} — {t('searchService.patentSearch.unavailable', 'Temporarily unavailable')}</strong>
                  <br />
                  <small>{t('searchService.patentSearch.description', 'AI agent explores EPO patent database, tunes queries, retrieves patent IDs. Option to stop after keyword search or continue to semantic analysis.')}</small>
                </button>
                <button className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50" onClick={() => onSelectSearchService('ops-direct')} style={{ padding: '1rem', textAlign: 'left' }}>
                  <strong>{t('searchService.opsDirect.button', 'EP Patent Retrieval — Direct Query')}</strong>
                  <br />
                  <small>{t('searchService.opsDirect.description', 'Structured Espacenet-style search form. Query OPS directly, preview results count, then download IDs+biblio or full text.')}</small>
                </button>
                <button className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50" onClick={() => onSelectSearchService('openalex-direct')} style={{ padding: '1rem', textAlign: 'left' }}>
                  <strong>{t('searchService.openalexDirect.button', 'Scientific Literature — Direct Query (OpenAlex)')}</strong>
                  <br />
                  <small>{t('searchService.openalexDirect.description', 'Structured OpenAlex search. Suggests relevant topics, query by keyword/author, preview count, then download metadata + abstracts to a KB.')}</small>
                </button>
                <button className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50" onClick={() => onSelectSearchService('extraction-fast')} style={{ padding: '1rem', textAlign: 'left' }}>
                  <strong>{t('searchService.extractionFast.button', 'RAGS — Deep KB Retrieval')}</strong>
                  <br />
                  <small>{t('searchService.extractionFast.description', 'Hybrid keyword + semantic search, then AI scoring of top results. Works on any KB size (up to 4000 candidates).')}</small>
                </button>
                <button className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50" onClick={() => onSelectSearchService('extraction-slow')} style={{ padding: '1rem', textAlign: 'left' }}>
                  <strong>{t('searchService.extractionSlow.button', 'DPDS — Document-per-Document Search')}</strong>
                  <br />
                  <small>{t('searchService.extractionSlow.description', 'AI individually reads and scores every document in the KB. Thorough analysis for collections up to 5000 documents.')}</small>
                </button>
                <button className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50" onClick={() => onSelectSearchService('cpc-search')} style={{ padding: '1rem', textAlign: 'left' }}>
                  <strong>{t('searchService.cpcSearch.button')}</strong>
                  <br />
                  <small>{t('searchService.cpcSearch.description')}</small>
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button type="button" className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 disabled:opacity-50" onClick={onCloseSearchServiceModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Document Modal (KB only) */}
      {showAddDocModal && entityType === 'kb' && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onCloseAddDocModal()}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2>{t('kbDetail.addDocumentsModal.title', 'Add Document to Knowledge Base')}</h2>
            <div className="upload-mode-selector" style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
              {(['patent', 'scipub', 'url', 'other'] as const).map((mode) => {
                const labels: Record<string, string> = {
                  patent: t('kbDetail.addDocumentsModal.tabPatents', 'Patents'),
                  scipub: t('kbDetail.addDocumentsModal.tabSciPub', 'Sci.Pub.'),
                  url: t('kbDetail.addDocumentsModal.tabWebPages', 'Web Pages'),
                  other: t('kbDetail.addDocumentsModal.tabOthers', 'Others'),
                };
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setUploadMode(mode)}
                    style={{
                      padding: '8px 18px',
                      border: 'none',
                      background: uploadMode === mode ? '#4a90e2' : 'transparent',
                      color: uploadMode === mode ? '#fff' : '#666',
                      cursor: 'pointer',
                      borderRadius: '4px 4px 0 0',
                      fontWeight: uploadMode === mode ? 'bold' : 'normal',
                    }}
                  >
                    {labels[mode]}
                  </button>
                );
              })}
            </div>
            <form onSubmit={onSubmitUpload} key={uploadMode}>
              {uploadMode !== 'url' ? (
                <>
                  {uploadMode === 'patent' && (
                    <div style={{ background: '#eef4ff', border: '1px solid #c7d9f8', borderRadius: '4px', padding: '8px 12px', marginBottom: '12px', fontSize: '13px', color: '#1a3a6b' }}>
                      {t('kbDetail.addDocumentsModal.patentHint', 'Bibliographic data (patent ID, inventors, CPC codes, etc.) will be automatically extracted after upload.')}
                    </div>
                  )}
                  {uploadMode === 'scipub' && (
                    <div style={{ background: '#eef4ff', border: '1px solid #c7d9f8', borderRadius: '4px', padding: '8px 12px', marginBottom: '12px', fontSize: '13px', color: '#1a3a6b' }}>
                      {t('kbDetail.addDocumentsModal.scipubHint', 'Bibliographic data (authors, journal, DOI, etc.) will be automatically extracted after upload.')}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label>{t('kbDetail.addDocumentsModal.selectLabel', 'Select Documents')} *</label>
                    <input type="file" multiple accept=".pdf,.doc,.docx,.txt" required onChange={(e) => setSelectedFiles(e.target.files)} />
                    <small>{t('kbDetail.addDocumentsModal.selectHint', 'Select one or more documents (PDF, DOC, DOCX, TXT)')}</small>
                  </div>
                  {selectedFiles && selectedFiles.length > 0 && (
                    <div className="file-preview">
                      <h4>{t('kbDetail.addDocumentsModal.selectedFiles', 'Selected Files:')}</h4>
                      <ul>
                        {Array.from(selectedFiles).map((file, idx) => (
                          <li key={idx}>{file.name} ({(file.size / 1024).toFixed(2)} KB)</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-1">
                    <label>{t('kbDetail.addDocumentsModal.urlLabel', 'Web Page URL')} *</label>
                    <input type="url" placeholder="https://example.com/article" value={pageUrl} onChange={(e) => setPageUrl(e.target.value)} required style={{ width: '100%', padding: '8px', fontSize: '14px' }} />
                    <small>{t('kbDetail.addDocumentsModal.urlHint', 'Enter the URL of the web page to capture and convert to PDF')}</small>
                  </div>
                  <div className="url-disclaimer" style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '12px', marginTop: '15px', fontSize: '13px', color: '#856404' }}>
                    <strong>{t('kbDetail.addDocumentsModal.legalTitle', 'Legal Disclaimer:')}</strong> {t('kbDetail.addDocumentsModal.legalText', 'You are responsible for ensuring you have the right to capture this content.')}
                  </div>
                  <div className="url-info" style={{ background: '#e7f3ff', border: '1px solid #4a90e2', borderRadius: '4px', padding: '12px', marginTop: '10px', fontSize: '13px', color: '#1a5490' }}>
                    <strong>{t('kbDetail.addDocumentsModal.whatHappensTitle', 'What happens:')}</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>{t('kbDetail.addDocumentsModal.whatHappens1', 'The web page will be fetched and cleaned')}</li>
                      <li>{t('kbDetail.addDocumentsModal.whatHappens2', 'Content will be converted to PDF for storage')}</li>
                      <li>{t('kbDetail.addDocumentsModal.whatHappens3', 'Text will be extracted for searchability')}</li>
                    </ul>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 disabled:opacity-50" onClick={onCloseAddDocModal} disabled={uploading}>Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50" disabled={uploading}>
                  {uploading ? (uploadMode === 'url' ? 'Capturing...' : 'Uploading...') : (uploadMode === 'url' ? 'Capture Web Page' : 'Upload Documents')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tech Watch Config Modal */}
      {showTechWatchConfig && entityType === 'sub-project' && entityId && (
        <TechWatchConfigModal
          isOpen={showTechWatchConfig}
          onClose={onCloseTechWatchConfig}
          subProjectId={entityId}
          onSaved={onTechWatchSaved}
          onDeleted={onTechWatchDeleted}
        />
      )}

      {/* Tech Watch Digest Panel */}
      {showTechWatchDigest && techWatchStatus?.exists && entityType === 'sub-project' && techWatchStatus?.watch_id && (
        <div style={{ position: 'fixed', right: 0, top: 56, bottom: 0, width: '80vw', background: '#fff',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.12)', zIndex: 900, overflowY: 'auto' }}>
          <TechWatchDigestPanel
            watchId={techWatchStatus.watch_id}
            onClose={onCloseTechWatchDigest}
            onEditWatch={onEditWatch}
          />
        </div>
      )}
    </>
  );
};

export default SCV2Modals;
